import React, { useEffect, useState } from "react";
import api, { resolveMedia } from "@/lib/api";
import MediaGallery from "@/components/MediaGallery";
import RichText from "@/components/RichText";
import useSEO, { stripHtml } from "@/lib/seo";

const HERO_IMG = "/assets/info/01-baugrube.jpg";
const STRIP_IMGS = [
  "/assets/info/07-absperrung.jpg",
  "/assets/info/11-parkplatz.jpg",
  "/assets/info/23-poller.jpg",
];

export default function Informationen() {
  const [home, setHome] = useState(null);
  useEffect(() => { api.get("/homepage").then((r) => setHome(r.data)); }, []);
  const info = home?.info || {};
  const blocks = info.blocks && info.blocks.length > 0 ? info.blocks : [];
  const gallery = (info.gallery || []).filter((g) => g && g.url);
  useSEO({
    title: info.heading || "Allgemeine Informationen",
    description: stripHtml(info.intro, 220) || "Allgemeine Informationen zum Quartier am Hornschen Tor in Detmold — Hintergründe zum Projekt, Bauphasen und Beteiligten.",
    path: "/informationen",
  });

  return (
    <div data-testid="info-page">
      {/* Hero-Bild als Auftakt — kompakt, mit weicher Verlaufsabdeckung unten */}
      <section className="relative h-[26vh] sm:h-[32vh] min-h-[200px] max-h-[360px] overflow-hidden bg-neutral-900">
        <img
          src={HERO_IMG}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover opacity-95"
          loading="eager"
        />
        <div
          className="absolute inset-0"
          style={{
            background:
              "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.15) 55%, rgba(255,255,255,0.95) 100%)",
          }}
        />
        <div className="container-narrow max-w-3xl relative h-full flex items-end pb-6 lg:pb-8">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[hsl(var(--brand-green-strong))] mb-2 drop-shadow-sm">
              Informationen
            </p>
            <h1 className="font-semibold text-4xl sm:text-5xl text-neutral-900 leading-[1.05]">
              {info.heading || "Allgemeine Informationen"}
            </h1>
          </div>
        </div>
      </section>

      <div className="container-narrow py-12 lg:py-16 max-w-3xl">
        {info.intro && <RichText html={info.intro} className="prose-quartier text-lg text-neutral-600 leading-relaxed mb-12 no-quote" />}

        {/* Kompakter 3er-Bild-Streifen als visuelle Pause */}
        <section className="not-prose mb-16" aria-label="Eindrücke" data-testid="info-photo-strip">
          <div className="grid grid-cols-3 gap-2 sm:gap-3">
            {STRIP_IMGS.map((src, i) => (
              <figure key={i} className="relative overflow-hidden bg-neutral-100 aspect-square">
                <img
                  src={src}
                  alt=""
                  aria-hidden="true"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 hover:scale-[1.03]"
                />
              </figure>
            ))}
          </div>
        </section>

        {gallery.length > 0 && (
          <section className="mb-16" data-testid="info-gallery">
            <MediaGallery items={gallery} testid="info-gallery-media" />
          </section>
        )}

        {blocks.length === 0 ? (
          <p className="text-neutral-500">Es wurden noch keine Inhalte veröffentlicht.</p>
        ) : (
          <div className="space-y-16">
            {blocks.map((b, i) => (
              <section key={i} data-testid={`info-section-${i}`}>
                {b.image_url && (
                  <div className="aspect-[16/9] overflow-hidden bg-neutral-100 mb-6">
                    <img src={resolveMedia(b.image_url)} alt={b.heading} className="w-full h-full object-cover" />
                  </div>
                )}
                <h2 className="font-semibold text-2xl sm:text-3xl mb-4">{b.heading}</h2>
                <RichText html={b.body} className="prose-quartier text-neutral-700" />
                <MediaGallery items={b.media || []} testid={`info-media-${i}`} />
              </section>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
